"use client";

/**
 * VaultMorph — a "converge / transform" delight animation: incoming item glyphs
 * fly in from the left and resolve into a center anchor, on a loop while it is
 * `running`. The on-brand depiction of "items being converted into an
 * experience" (e.g. the sidebar Vault affordance while an experience is
 * generating / updating).
 *
 * Adapted from the React Bits starter component `magic-transform`
 * ("Documents fly in and resolve into results") and renamed to a domain name
 * per this repo's convention (cf. `Folder`, `MorphingRail`, `AnimatedList`).
 * Reimplemented on `motion/react` (the registry source is license-gated): all
 * palette swapped for theme tokens, `forwardRef` on the outer wrapper, `cva`
 * size variants. Honors `prefers-reduced-motion` — the fly-in collapses to a
 * static centered frame (just the resolved center).
 *
 * Generic by design: `center` is any node (defaults to the AISquare mark) and
 * `items` are the incoming glyphs (default three document glyphs).
 *
 * @example
 *   <VaultMorph running={isGenerating} items={vaultDocs.map(d => ({ id: d.id, label: d.name }))} />
 */
import * as React from "react";
import { motion } from "motion/react";
import { FileText } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { AikoMark } from "./AikoMark";

const vaultMorphVariants = cva("relative flex shrink-0 items-center justify-center overflow-hidden", {
  variants: {
    size: {
      sm: "h-12 w-20",
      md: "h-16 w-28",
      lg: "h-24 w-40",
    },
  },
  defaultVariants: { size: "md" },
});

export interface VaultMorphItem {
  id: string;
  /** Accessible label for the glyph (e.g. the document name). */
  label?: string;
  /** Custom glyph; defaults to a document icon. */
  icon?: React.ReactNode;
}

export interface VaultMorphProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof vaultMorphVariants> {
  /** Incoming glyphs. Defaults to three generic document glyphs. */
  items?: VaultMorphItem[];
  /** The center anchor the items resolve into. Defaults to the AISquare mark. */
  center?: React.ReactNode;
  /** Run the converge loop. `false` (or reduced motion) renders the static
      resolved center only. */
  running?: boolean;
  /** Accessible label for the whole figure. */
  "aria-label"?: string;
}

const DEFAULT_ITEMS: VaultMorphItem[] = [{ id: "d1" }, { id: "d2" }, { id: "d3" }];

export const VaultMorph = React.forwardRef<HTMLDivElement, VaultMorphProps>(function VaultMorph(
  {
    items = DEFAULT_ITEMS,
    center,
    running = true,
    size,
    className,
    "aria-label": ariaLabel = "Converting items into an experience",
    ...props
  },
  ref,
) {
  const reduced = useReducedMotion();
  const lively = running && reduced !== true;
  const n = items.length;

  return (
    <div
      ref={ref}
      data-slot="vault-morph"
      role="img"
      aria-label={ariaLabel}
      aria-busy={running || undefined}
      className={cn(vaultMorphVariants({ size }), className)}
      {...props}
    >
      {/* Incoming glyphs: fly in from the left, fan offset by index, and resolve
          into the center (scale + fade out as they merge). Hidden in the static
          frame. */}
      {lively
        ? items.map((item, i) => {
            const fan = (i - (n - 1) / 2) * 10;
            return (
              <motion.span
                key={item.id}
                data-slot="vault-morph-item"
                aria-hidden
                className="absolute flex size-5 items-center justify-center rounded-[5px] border border-border bg-card text-muted-foreground shadow-sm [&_svg]:size-3"
                initial={false}
                animate={{
                  x: ["-180%", "0%", "0%"],
                  y: [`${fan}px`, "0px", "0px"],
                  opacity: [0, 1, 0],
                  scale: [0.85, 1, 0.4],
                }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.3,
                  times: [0, 0.6, 1],
                }}
              >
                {item.icon ?? <FileText aria-hidden />}
              </motion.span>
            );
          })
        : null}

      {/* Center anchor — gently pulses while running to feel like it is
          absorbing the incoming items. */}
      <motion.span
        data-slot="vault-morph-center"
        className="relative z-10 flex items-center justify-center"
        animate={lively ? { scale: [1, 1.08, 1] } : { scale: 1 }}
        transition={lively ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" } : { duration: 0 }}
      >
        {center ?? <AikoMark size={size === "lg" ? "md" : "sm"} aria-hidden />}
      </motion.span>
    </div>
  );
});
